import {
  ArmyList,
  ArmyListUnit,
  ArmyListUnitOption,
  ConstraintScope,
  IConstrainedObject,
  UnitOption,
  UnitOptionAvailability,
  UnitOptionAvailabilityAction,
  UnitOptionAvailabilityActivator,
  UnitOptionUsePoint,
} from '@Models';
import { OptionActivation } from './optionActivation';
import { allSeletedElement } from '@Selectors/builder/myArmyList/common/constraints/elements/selectable/all-seleted-element';
import { compareValue } from '@Selectors/builder/myArmyList/common/constraints/conditions/modifier-is-active';

export const optionIsActive = (
  armyListUnit: ArmyListUnit,
  armyList: ArmyList,
  armyListUnitOptions: Array<ArmyListUnitOption>,
  option: UnitOption,
  unitOptions: Array<UnitOption>,
): boolean => {
  const allArmyListUnitOptions = armyList.army_list_units
    .filter((item) => item.is_enabled && !item.isDeleted)
    .filter((item) => item.unit_id != option.unit_id)
    .filter((item) => armyListUnit.id == '' || item.id != armyListUnit.id)
    .flatMap((item) => item.army_list_unit_unit_options)
    .concat(armyListUnitOptions);

  return (
    [
      checkOptionParent(option, unitOptions, allArmyListUnitOptions),
      checkConstrainedObjectHidden(option, armyList, armyListUnit),
      checkOptionActivators(option, unitOptions, allArmyListUnitOptions),
      checkOptionDesactivators(option, unitOptions, allArmyListUnitOptions),
      checkUnitSizeActivators(option, armyListUnit.size),
      armyOrganisationActivation(option, armyList.army_organisation_id),
      armyOrganisationDesactivation(option, armyList.army_organisation_id),
    ]
      .flatMap((items) => items)
      .filter((item) => item != OptionActivation.UNKNOW)
      .map((item) => item === OptionActivation.ACTIVE)
      .reduce((a, b) => a && b, true) ?? true
  );
};

export const checkOptionParent = (
  option: UnitOption,
  unitOptions: Array<UnitOption>,
  armyListUnitOptions: Array<ArmyListUnitOption>,
): OptionActivation => {
  if (!option.parent_id) return OptionActivation.UNKNOW;

  const parent = unitOptions.find((item) => item.id === option.parent_id);
  if (!parent) return OptionActivation.DESACTIVE;

  if (
    parent.use_points === UnitOptionUsePoint.NoCost ||
    parent.use_points === UnitOptionUsePoint.NoLimit ||
    parent.use_points === UnitOptionUsePoint.UpTo
  )
    return OptionActivation.ACTIVE;

  if (
    armyListUnitOptions.some((item) => item.unit_option_id === option.parent_id)
  ) {
    if (!parent.parent_id) return OptionActivation.ACTIVE;

    switch (checkOptionParent(parent, unitOptions, armyListUnitOptions)) {
      case OptionActivation.UNKNOW:
      case OptionActivation.ACTIVE:
        return OptionActivation.ACTIVE;
    }
  }

  return OptionActivation.DESACTIVE;
};

export const checkConstrainedObjectHidden = (
  option: IConstrainedObject,
  armyList: ArmyList,
  armyListUnit: ArmyListUnit,
): OptionActivation[] => {
  const modifiers = option.modifiers
    .map((modifier) => {
      const modifierIsActive = modifier.conditions.some((condition) => {
        const ignoreArmyListUnitIds: string[] = [];

        if (condition.scope === ConstraintScope.Parent) {
          armyList.army_list_units
            .filter((alu) => alu.id !== armyListUnit.id)
            .map((alu) => alu.id)
            .forEach((alu) => ignoreArmyListUnitIds.push(alu));
        }

        const elementCount = allSeletedElement(
          armyList,
          [],
          [],
          ignoreArmyListUnitIds,
        )
          .filter(
            (element) =>
              condition.childId === element.constrained.id &&
              condition.childType === element.constrained.type,
          )
          .map((element) => element.count)
          .reduce((acc, count) => acc + count, 0);

        return compareValue(condition, elementCount);
      });

      if (modifierIsActive) {
        if (modifier.value === 'false' || modifier.value === '0')
          return OptionActivation.ACTIVE;

        return OptionActivation.DESACTIVE;
      }

      return OptionActivation.UNKNOW;
    })
    .filter((activation) => OptionActivation.UNKNOW !== activation);

  if (option.hidden && modifiers.length === 0)
    modifiers.push(OptionActivation.DESACTIVE);

  return modifiers;
};

const checkOptionActivators = (
  option: UnitOption,
  unitOptions: Array<UnitOption>,
  armyListUnitOptions: Array<ArmyListUnitOption>,
): OptionActivation => {
  const availabilities = option.availabilities.filter(
    (item) =>
      item.action == UnitOptionAvailabilityAction.Enable &&
      (item.activator === UnitOptionAvailabilityActivator.SameUnit ||
        item.activator === UnitOptionAvailabilityActivator.OtherUnit),
  );
  if (availabilities.length === 0) {
    return OptionActivation.UNKNOW;
  }

  const noSelectableOptions = unitOptions.filter((item) =>
    [
      UnitOptionUsePoint.NoCost,
      UnitOptionUsePoint.NoLimit,
      UnitOptionUsePoint.UpTo,
    ].includes(item.use_points),
  );

  const activators = (item: UnitOptionAvailability): boolean =>
    armyListUnitOptions.some(
      (option) => option.unit_option_id === item.unitOptionId,
    ) || noSelectableOptions.some((o) => o.id === item.unitOptionId);

  if (
    (option.useAllActivators && !availabilities.every(activators)) ||
    (!option.useAllActivators && !availabilities.some(activators))
  ) {
    return OptionActivation.DESACTIVE;
  }

  return OptionActivation.ACTIVE;
};

const checkUnitSizeActivators = (
  option: UnitOption,
  size: number,
): OptionActivation => {
  const availabilities = option.availabilities.filter(
    (item) =>
      item.action == UnitOptionAvailabilityAction.Enable &&
      item.activator === UnitOptionAvailabilityActivator.SizeMin &&
      item.sizeMin,
  );
  if (availabilities.length === 0) {
    return OptionActivation.UNKNOW;
  }

  const activators = (item: UnitOptionAvailability): boolean =>
    item.sizeMin! <= size;

  if (
    (option.useAllActivators && !availabilities.every(activators)) ||
    (!option.useAllActivators && !availabilities.some(activators))
  ) {
    return OptionActivation.DESACTIVE;
  }

  return OptionActivation.ACTIVE;
};

const checkOptionDesactivators = (
  option: UnitOption,
  unitOptions: Array<UnitOption>,
  armyListUnitOptions: Array<ArmyListUnitOption>,
): OptionActivation => {
  const availabilities = option.availabilities.filter(
    (item) => item.action == UnitOptionAvailabilityAction.Disable,
  );
  if (availabilities.length === 0) {
    return OptionActivation.UNKNOW;
  }

  const desactivators = (item: UnitOptionAvailability): boolean =>
    armyListUnitOptions.some(
      (option) => option.unit_option_id === item.unitOptionId,
    );

  if (
    (option.useAllDesactivators && availabilities.every(desactivators)) ||
    (!option.useAllDesactivators && availabilities.some(desactivators))
  ) {
    return OptionActivation.DESACTIVE;
  }

  return OptionActivation.ACTIVE;
};

const armyOrganisationActivation = (
  option: UnitOption,
  armyOrganisationId: number,
): OptionActivation => {
  const availabilities = option.availabilities.filter(
    (item) =>
      item.action == UnitOptionAvailabilityAction.Enable &&
      item.activator === UnitOptionAvailabilityActivator.ArmyOrganisation &&
      item.armyOrganisationId,
  );
  if (availabilities.length === 0) {
    return OptionActivation.UNKNOW;
  }

  const activators = (item: UnitOptionAvailability): boolean =>
    item.armyOrganisationId! === armyOrganisationId;

  if (
    (option.useAllActivators && !availabilities.every(activators)) ||
    (!option.useAllActivators && !availabilities.some(activators))
  ) {
    return OptionActivation.DESACTIVE;
  }

  return OptionActivation.ACTIVE;
};

const armyOrganisationDesactivation = (
  option: UnitOption,
  armyOrganisationId: number,
): OptionActivation => {
  const availabilities = option.availabilities.filter(
    (item) =>
      item.action == UnitOptionAvailabilityAction.Disable &&
      item.activator === UnitOptionAvailabilityActivator.ArmyOrganisation &&
      item.armyOrganisationId,
  );
  if (availabilities.length === 0) {
    return OptionActivation.UNKNOW;
  }

  const desactivators = (item: UnitOptionAvailability): boolean =>
    item.armyOrganisationId! === armyOrganisationId;

  if (
    (option.useAllDesactivators && availabilities.every(desactivators)) ||
    (!option.useAllDesactivators && availabilities.some(desactivators))
  ) {
    return OptionActivation.DESACTIVE;
  }

  return OptionActivation.ACTIVE;
};
